import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actions from './actions';
import { SettingsService } from './service';
import * as actionsNotification from '../InitComponent/actions';
import {
  NOTIFICATION_CHANGES_SAVED,
  NOTIFICATION_CHANGES_NOT_SAVED,
  NOTIFICATION_CHANGES_NOT_SAVED_GLOVO_TIME,
} from '../../global/constants';
import { DeliveryProvider, DeliveryProviderName } from './types';

interface SettingsParam {
  type: string;
  payload: { delivery_enabled: boolean; postEffect: any };
}

interface CardPaymentSettingsParam {
  type: string;
  payload: { enabled: boolean; postEffect: any };
}

interface GetConfigResponse {
  result: string | null;
  status: string;
}

interface GetCardPaymentResponse {
  enabled: boolean | null;
  status: string;
}

interface GlovoSEnableSource {
  type: string;
  payload: { until: string; postEffect: any };
}

interface WoltEnablePayload {
  type: string;
  payload: { postEffect: any };
}

interface GetDeliveryProvidersResponse {
  status: string;
  result: DeliveryProvider[];
}

interface SetDeliveryProviderParam {
  type: string;
  payload: {
    provider: DeliveryProviderName;
    enabled: boolean;
    postEffect?: () => void;
  };
}

export function* getConfigSaga() {
  try {
    const response: GetConfigResponse = yield call(SettingsService.getConfig);
    yield put(
      actions.getConfig.success({
        data: response.result,
      })
    );
  } catch (error) {
    yield put(actions.getConfig.error(error));
  }
}

export function* setConfigSaga({ payload }: SettingsParam) {
  try {
    // @ts-ignore
    const response: any = yield call(SettingsService.setConfig, payload);
    yield put(actions.setConfig.success(response.result));
    yield put(
      actionsNotification.addApplicationNotification({
        content: NOTIFICATION_CHANGES_SAVED,
        canBeClosed: true,
        type: 'success',
      })
    );
    if (payload.postEffect) {
      payload.postEffect();
    }
  } catch (error) {
    yield put(
      actionsNotification.addApplicationNotification({
        content: NOTIFICATION_CHANGES_NOT_SAVED,
        canBeClosed: true,
        type: 'error',
      })
    );
    yield put(actions.setConfig.error(error));
  }
}

export function* getCardPaymentSaga() {
  try {
    const response: GetCardPaymentResponse = yield call(
      SettingsService.getCardPayment
    );
    yield put(actions.getCardPayment.success(response?.enabled));
  } catch (error) {
    yield put(actions.getCardPayment.error(error));
  }
}

export function* setCardPaymentSaga({ payload }: CardPaymentSettingsParam) {
  try {
    // @ts-ignore
    const response: any = yield call(SettingsService.setCardPayment, payload);
    yield put(actions.setCardPayment.success(response.result));
    yield put(
      actionsNotification.addApplicationNotification({
        content: NOTIFICATION_CHANGES_SAVED,
        canBeClosed: true,
        type: 'success',
      })
    );
    if (payload.postEffect) {
      payload.postEffect();
    }
  } catch (error) {
    yield put(
      actionsNotification.addApplicationNotification({
        content: NOTIFICATION_CHANGES_NOT_SAVED,
        canBeClosed: true,
        type: 'error',
      })
    );
    yield put(actions.setCardPayment.error(error));
  }
}

export function* getGlovoEnabledStatusSaga() {
  try {
    const response: GetCardPaymentResponse = yield call(
      SettingsService.getGlovoEnabledStatus
    );
    yield put(actions.getGlovoEnabledStatus.success(response));
  } catch (error) {
    yield put(actions.getGlovoEnabledStatus.error(error));
  }
}

export function* setEnambleGlovoSaga({ payload }: GlovoSEnableSource) {
  try {
    const response: { status: string } = yield call(
      SettingsService.setEnambleGlovo
    );

    yield put(
      actionsNotification.addApplicationNotification({
        content: 'Глово в аптеках включен!',
        canBeClosed: true,
        type: 'success',
      })
    );

    if (payload.postEffect) {
      payload.postEffect();
    }
    yield put(actions.setEnambleGlovo.success(response.status));
  } catch (error) {
    yield put(
      actionsNotification.addApplicationNotification({
        content: NOTIFICATION_CHANGES_NOT_SAVED,
        canBeClosed: true,
        type: 'error',
      })
    );

    yield put(actions.setEnambleGlovo.error(error));
  }
}

export function* setDisableGlovoSaga({ payload }: GlovoSEnableSource) {
  try {
    const response: { result: string } = yield call(
      SettingsService.setDisableGlovo,
      payload.until
    );

    yield put(
      actionsNotification.addApplicationNotification({
        content: `Глово в аптеках выключен до ${new Date(
          payload.until
        ).toLocaleString()}!`,
        canBeClosed: true,
        type: 'success',
      })
    );

    if (payload.postEffect) {
      payload.postEffect();
    }
    yield put(actions.setDisableGlovo.success(response.result));
  } catch (error) {
    yield put(
      actionsNotification.addApplicationNotification({
        content:
          (error as any).response.data.message ===
          'until time must be in the future'
            ? NOTIFICATION_CHANGES_NOT_SAVED_GLOVO_TIME
            : NOTIFICATION_CHANGES_NOT_SAVED,
        canBeClosed: true,
        type: 'error',
      })
    );

    yield put(actions.setDisableGlovo.error(error));
  }
}

export function* setWoltEnabledSaga({ payload }: WoltEnablePayload) {
  try {
    const res: {
      status: string;
      redirect_url: string;
    } = yield call(SettingsService.setWoltEnabled);
    payload.postEffect(res.redirect_url);
    yield put(actions.setWoltEnabled.success(res));
  } catch (error) {
    yield put(actions.setWoltEnabled.error(error));
  }
}

export function* getDeliveryProvidersSaga() {
  try {
    const response: GetDeliveryProvidersResponse = yield call(
      SettingsService.getDeliveryProviders
    );
    yield put(actions.getDeliveryProviders.success(response.result));
  } catch (error) {
    yield put(actions.getDeliveryProviders.error(error));
  }
}

export function* setDeliveryProviderEnabledSaga({
  payload,
}: SetDeliveryProviderParam) {
  try {
    const response: {
      status: string;
      result: DeliveryProvider;
    } = yield call(
      SettingsService.setDeliveryProviderEnabled,
      payload.provider,
      payload.enabled
    );
    yield put(actions.setDeliveryProviderEnabled.success(response.result));
    yield put(
      actionsNotification.addApplicationNotification({
        content: NOTIFICATION_CHANGES_SAVED,
        canBeClosed: true,
        type: 'success',
      })
    );
    if (payload.postEffect) {
      payload.postEffect();
    }
  } catch (error) {
    yield put(
      actionsNotification.addApplicationNotification({
        content: NOTIFICATION_CHANGES_NOT_SAVED,
        canBeClosed: true,
        type: 'error',
      })
    );
    yield put(actions.setDeliveryProviderEnabled.error(error));
  }
}

export function* settingPageWatcherSaga() {
  yield all([takeLatest(actions.getConfig.REQUEST, getConfigSaga)]);
  yield all([takeLatest(actions.setConfig.REQUEST, setConfigSaga)]);
  yield all([takeLatest(actions.getCardPayment.REQUEST, getCardPaymentSaga)]);
  yield all([takeLatest(actions.setCardPayment.REQUEST, setCardPaymentSaga)]);
  yield all([
    takeLatest(
      actions.getGlovoEnabledStatus.REQUEST,
      getGlovoEnabledStatusSaga
    ),
  ]);
  yield all([takeLatest(actions.setEnambleGlovo.REQUEST, setEnambleGlovoSaga)]);
  yield all([takeLatest(actions.setDisableGlovo.REQUEST, setDisableGlovoSaga)]);
  yield all([takeLatest(actions.setWoltEnabled.REQUEST, setWoltEnabledSaga)]);
  yield all([
    takeLatest(actions.getDeliveryProviders.REQUEST, getDeliveryProvidersSaga),
  ]);
  // takeEvery, а не takeLatest: за одно сохранение может уйти до 3 запросов подряд,
  // и takeLatest отменил бы обработку предыдущих
  yield all([
    takeEvery(
      actions.setDeliveryProviderEnabled.REQUEST,
      setDeliveryProviderEnabledSaga
    ),
  ]);
}
